import React, { useEffect } from "react";
import  { useDispatch, useSelector } from "react-redux";
import { Blade, Button, ProgressIndicator, ReduxControls } from "rhombus-react";
import { reduxForm, Field, getFormValues, change, getFormSyncErrors } from "redux-form";

import useActiveCreative from "hooks/useActiveCreative";

import { submitCreativesToQA } from 'reducers/Creatives';
import { updateLibrary } from "reducers/LibraryProperties";
import { closeModal } from 'reducers/Modal';
import { addNotification } from "reducers/Notifications";

import { isYouTube } from "utils/CreativeActions";
import { copyToClipboard, labelWithCount } from "utils/common";
import { SUPPRESS_SUCCESS_GROWL } from "utils/constants";

import InfoBanner from "widgets/InfoBanner";
import CreativesTable from 'widgets/table/CreativesTable';
import YouTubeToS from "widgets/modals/creatives/social/YouTubeToS";
import SubmitCreativeEmailField from "widgets/modals/creatives/SubmitCreativeEmailField";
import SubmitCreativeRecipientsFields from 'widgets/modals/creatives/SubmitCreativeRecipientsFields';
import { isEmail } from 'widgets/forms/validators/validators';

import "./SubmitCreatives.css";

const SUBMIT_CREATIVES_TO_QA_FORM = 'SUBMIT_CREATIVES_TO_QA_FORM';

function SubmitCreatives(props){
	let dispatch = useDispatch()
		, { creatives = [], libraryId } = props
		, { isUpdating, formValues, primaryEmail, mediaBuyerEmail, formSyncErrors } = useSelector(state => ({
			isUpdating: state.creatives.isUpdating || !state.libraryProperties.isLoaded,
			formValues: getFormValues(SUBMIT_CREATIVES_TO_QA_FORM)(state),
			primaryEmail: state.libraryProperties.email,
			mediaBuyerEmail: state.libraryProperties.mediaBuyerEmail,
			formSyncErrors: getFormSyncErrors(SUBMIT_CREATIVES_TO_QA_FORM)(state),
		}))
		, hasMediaBuyerEmail  = !!mediaBuyerEmail
		, isYT = creatives.some(creative => isYouTube(creative.adType))
		, noMediaBuyerMessage = null
		, modalTitle = "Submit to QA"
		, isMulti = creatives.length > 1
		, onClose = () => dispatch(closeModal())
		, onSubmit = () => {
			return new Promise((resolve) => {
				/**
				 * Some libraries are created without a media buyer email,
				 * so we want to update the library before submitting the ad
				 */
				if (!hasMediaBuyerEmail && formValues.mediaBuyerEmail) {
					dispatch(updateLibrary(
						libraryId, { mediaBuyerEmail: formValues.mediaBuyerEmail }, { meta: SUPPRESS_SUCCESS_GROWL }
					)).then(resp => resolve(resp));
				} else {
					resolve({ success: true });
				}
			}).then((resp) => {
				if (resp.success) {
					dispatch(submitCreativesToQA({
						libraryId,
						creativeIds: creatives.map(creative => creative.id),
						mediaBuyerEmail: formValues.mediaBuyerEmail,
						sendMediaBuyerEmail: formValues.sendMediaBuyerEmail,
						primaryEmail: formValues.primaryEmail,
						sendPrimaryEmail: formValues.sendPrimaryEmail,
						otherRecipients: formValues.otherRecipients,
						notes: formValues.notes,
					})).then((response) => {
						if (response?.success) {
							return dispatch(closeModal());
						}
					});
				}
			});
		}
		, numErrors = Object.keys(formSyncErrors).length
		, otherRecipients = formValues?.otherRecipients || []
		, hasAtLeastOneEmail = !!mediaBuyerEmail || !!primaryEmail || otherRecipients.some(email => email && isEmail(email))
		, isValid = hasAtLeastOneEmail && numErrors === 0
		;

	useActiveCreative(creatives[0]?.id);

	useEffect(() => {
		dispatch(change(SUBMIT_CREATIVES_TO_QA_FORM, 'primaryEmail', primaryEmail));
		dispatch(change(SUBMIT_CREATIVES_TO_QA_FORM, 'mediaBuyerEmail', mediaBuyerEmail));
		dispatch(change(SUBMIT_CREATIVES_TO_QA_FORM, 'sendMediaBuyerEmail', !!mediaBuyerEmail));
	}, []);

	if (isMulti) {
		modalTitle = `Submit to QA - ${creatives.length} Creatives`;
	} else if (creatives.length === 1) {
		modalTitle = `Submit to QA - ${creatives[0].name}`;
	}

	if (!hasMediaBuyerEmail) {
		noMediaBuyerMessage = (
			<InfoBanner className="media-buyer-message">
				<strong>Please Note:</strong> Media Buyer email must be added below before submitting creative to QA
			</InfoBanner>
		);
	}

	const handleCopy = () => {
		const names = creatives.map(creative => creative.name).join("\n");

		copyToClipboard(names)
			.then(() => {
				dispatch(addNotification({
					type: "success",
					messageHeader: "Copy Names",
					message: "Creative Names Copied",
				}));
			});
	};

	const renderContent = () => (
		<div className="submit-creatives-modal-content">
			<ProgressIndicator isLoading={isUpdating} />

			{isMulti && (
				<div className="creatives-table-wrapper">
					<CreativesTable creatives={creatives} shouldLinkPreview={false} />
				</div>
			)}

			<div className="submit-creatives-form-wrapper">
				{noMediaBuyerMessage}
				<SubmitCreativeEmailField
					email={mediaBuyerEmail}
					emailType="mediaBuyer"
					initial={hasMediaBuyerEmail}
				/>
				<SubmitCreativeEmailField email={primaryEmail} emailType="primary" />
				<div className="submit-creative-email-array">
					<SubmitCreativeRecipientsFields />
				</div>
				<div className="submit-creative-textarea">
					<Field
						name="notes"
						label="Notes"
						type="textarea"
						placeholder="Add any notes that would be helpful..."
						component={ ReduxControls.Input }
					/>
				</div>
				{isYT ? <YouTubeToS label="Submit" /> : null}
			</div>
		</div>
	);

	const renderFooter = () => ([
		<Button key="cancel" theme="grey" disabled={isUpdating} onClick={onClose}>Cancel</Button>,
		<div key="right-buttons" className="right-buttons">
			<Button key="copy" onClick={handleCopy}>Copy Names</Button>
			<Button key="submit" theme="green" disabled={isUpdating || !isValid} onClick={onSubmit}>
				{labelWithCount("Submit", creatives.length)}
			</Button>
		</div>
	]);

	return (
		<Blade
			type={"blade"}
			setAppTitle={false}
			className={`submit-creatives-blade ${isMulti ? 'submit-multi' : 'submit-single'}`}
			title={modalTitle}
			allowFullScreen={false}
			xClose
			bladeProps={{ verticallyCenter: true }}
			onClose={onClose}
			renderContent={renderContent}
			renderFooter={renderFooter}
		/>
	);
}

SubmitCreatives.displayName = "SubmitCreatives";

export default reduxForm({
	form: SUBMIT_CREATIVES_TO_QA_FORM,
	initialValues: {
		mediaBuyerEmail: '',
		sendMediaBuyerEmail: false,
		primaryEmail: '',
		sendPrimaryEmail: true,
		otherRecipients: [''],
		notes: '',
	},
})(SubmitCreatives);
